import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API } from '../App';
import Layout from '../components/Layout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  FileText, Download, TrendingUp, Users, Award, AlertTriangle,
  BarChart2, CheckCircle2, ChevronLeft, ChevronRight, FileDown,
} from 'lucide-react';
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend,
  RadarChart, Radar, PolarGrid, PolarAngleAxis,
} from 'recharts';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

const COLORS = ['#4338CA', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#06B6D4'];

/* ── small helpers ───────────────────────────────────────── */
const scoreColor = (s) =>
  s >= 80 ? 'text-emerald-600' : s >= 60 ? 'text-amber-600' : 'text-rose-600';
const scoreBg = (s) =>
  s >= 80 ? 'bg-emerald-50 border-emerald-200 text-emerald-700'
          : s >= 60 ? 'bg-amber-50 border-amber-200 text-amber-700'
                    : 'bg-rose-50 border-rose-200 text-rose-700';

function KPICard({ label, value, sub, icon: Icon, accent }) {
  return (
    <Card className="p-5 border border-slate-200 shadow-sm">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs font-medium text-slate-500 mb-1">{label}</p>
          <p className={`text-3xl font-bold ${accent || 'text-slate-900'}`}>{value}</p>
          {sub && <p className="text-xs text-slate-500 mt-1">{sub}</p>}
        </div>
        <div className={`p-2 rounded-lg ${accent ? 'bg-indigo-50' : 'bg-slate-50'}`}>
          <Icon size={20} className={accent || 'text-slate-500'} />
        </div>
      </div>
    </Card>
  );
}

const Reports = () => {
  const navigate = useNavigate();

  /* ── state ───────────────────────────────────────────────── */
  const [kpi, setKpi] = useState(null);
  const [teams, setTeams] = useState([]);
  const [agents, setAgents] = useState([]);
  const [sops, setSops] = useState([]);
  const [trends, setTrends] = useState([]);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [myAgent, setMyAgent] = useState(null);

  /* ── filters ─────────────────────────────────────────────── */
  const [filters, setFilters] = useState({
    start_date: '', end_date: '', agent_id: '', team_id: '', sop_id: '',
    min_score: '', max_score: '',
  });
  const PAGE_SIZE = 15;

  /* ── fetch ───────────────────────────────────────────────── */
  const buildParams = useCallback((extra = {}) => {
    const p = {};
    if (filters.start_date) p.start_date = filters.start_date;
    if (filters.end_date)   p.end_date   = filters.end_date;
    if (filters.agent_id)   p.agent_id   = filters.agent_id;
    if (filters.team_id)    p.team_id    = filters.team_id;
    if (filters.sop_id)     p.sop_id     = filters.sop_id;
    if (filters.min_score)  p.min_score  = filters.min_score;
    if (filters.max_score)  p.max_score  = filters.max_score;
    return { ...p, ...extra };
  }, [filters]);

  const fetchAll = useCallback(async (pg = 1) => {
    setLoading(true);
    try {
      const [kpiRes, rowsRes, trendsRes] = await Promise.all([
        axios.get(`${API}/reports/dashboard`,    { params: buildParams() }),
        axios.get(`${API}/reports/assessments`,  { params: buildParams({ page: pg, page_size: PAGE_SIZE }) }),
        axios.get(`${API}/analytics/trends`,     { params: { days: 30 } }),
      ]);
      setKpi(kpiRes.data);
      setRows(rowsRes.data.rows);
      setTotal(rowsRes.data.total);
      setTrends(trendsRes.data);
    } catch (err) {
      console.error('Reports fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [buildParams]);

  useEffect(() => {
    fetchAll(1);
    setPage(1);
  }, [fetchAll]);

  useEffect(() => {
    const fetchMeta = async () => {
      try {
        const [tRes, aRes, sRes, meRes] = await Promise.all([
          axios.get(`${API}/teams`),
          axios.get(`${API}/agents`),
          axios.get(`${API}/sops`),
          axios.get(`${API}/agents/me`),
        ]);
        setTeams(tRes.data);
        setAgents(aRes.data);
        setSops(sRes.data);
        if (meRes.data) setMyAgent(meRes.data);
      } catch (err) {
        console.error('Meta fetch error:', err);
      }
    };
    fetchMeta();
  }, []);

  const handlePageChange = (newPage) => {
    setPage(newPage);
    fetchAll(newPage);
  };

  /* ── export ──────────────────────────────────────────────── */
  const handleExportCSV = async () => {
    try {
      const resp = await axios.get(`${API}/reports/export-csv`, {
        params: buildParams(),
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([resp.data]));
      const a = document.createElement('a');
      a.href = url; a.download = 'assessments_report.csv'; a.click();
      toast.success('CSV exported!');
    } catch {
      toast.error('Export failed');
    }
  };

  const handleExportPDF = async (callId) => {
    try {
      const resp = await axios.get(`${API}/assessments/${callId}/export-pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([resp.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url; a.download = `audit_${callId.slice(0, 8)}.pdf`; a.click();
      toast.success('PDF downloaded!');
    } catch {
      toast.error('PDF export failed');
    }
  };

  const handleExportRowCSV = async (callId) => {
    try {
      const resp = await axios.get(`${API}/assessments/${callId}/export-csv`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([resp.data]));
      const a = document.createElement('a');
      a.href = url; a.download = `audit_${callId.slice(0, 8)}.csv`; a.click();
      toast.success('Audit CSV downloaded!');
    } catch {
      toast.error('CSV export failed');
    }
  };

  const totalPages = Math.ceil(total / PAGE_SIZE);

  /* ─────────────────────────────────────────────────────────
     RENDER
  ────────────────────────────────────────────────────────── */
  return (
    <Layout>
      <div className="space-y-8 fade-in" data-testid="reports-page">

        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-4xl font-bold text-slate-900 mb-1" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Reports & Analytics
            </h1>
            {myAgent && (
              <p className="text-sm text-indigo-600 font-medium">
                Showing your performance — {myAgent.name}
              </p>
            )}
            {!myAgent && <p className="text-slate-600">Comprehensive performance insights</p>}
          </div>
          <Button onClick={handleExportCSV} className="bg-[#4338CA] hover:bg-[#3730A3] text-white"
            data-testid="export-csv-button">
            <Download size={16} className="mr-2" />Export Full CSV
          </Button>
        </div>

        {/* ── Filters ─────────────────────────────────────────── */}
        <Card className="p-4 border border-slate-200 shadow-sm">
          <p className="text-sm font-semibold text-slate-700 mb-3">Filters</p>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
            <div className="col-span-1">
              <label className="text-xs text-slate-500 mb-1 block">From</label>
              <Input type="date" value={filters.start_date}
                onChange={(e) => setFilters({ ...filters, start_date: e.target.value })}
                className="h-8 text-xs" />
            </div>
            <div className="col-span-1">
              <label className="text-xs text-slate-500 mb-1 block">To</label>
              <Input type="date" value={filters.end_date}
                onChange={(e) => setFilters({ ...filters, end_date: e.target.value })}
                className="h-8 text-xs" />
            </div>
            {!myAgent && (
              <div className="col-span-1">
                <label className="text-xs text-slate-500 mb-1 block">Agent</label>
                <Select value={filters.agent_id || 'all'}
                  onValueChange={(v) => setFilters({ ...filters, agent_id: v === 'all' ? '' : v })}>
                  <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="All" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Agents</SelectItem>
                    {agents.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            {!myAgent && (
              <div className="col-span-1">
                <label className="text-xs text-slate-500 mb-1 block">Team</label>
                <Select value={filters.team_id || 'all'}
                  onValueChange={(v) => setFilters({ ...filters, team_id: v === 'all' ? '' : v })}>
                  <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="All" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Teams</SelectItem>
                    {teams.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="col-span-1">
              <label className="text-xs text-slate-500 mb-1 block">SOP</label>
              <Select value={filters.sop_id || 'all'}
                onValueChange={(v) => setFilters({ ...filters, sop_id: v === 'all' ? '' : v })}>
                <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="All" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All SOPs</SelectItem>
                  {sops.map((s) => <SelectItem key={s.id} value={s.id}>{s.title}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-1">
              <label className="text-xs text-slate-500 mb-1 block">Min Score</label>
              <Input type="number" placeholder="0" min="0" max="100" value={filters.min_score}
                onChange={(e) => setFilters({ ...filters, min_score: e.target.value })}
                className="h-8 text-xs" />
            </div>
            <div className="col-span-1">
              <label className="text-xs text-slate-500 mb-1 block">Max Score</label>
              <Input type="number" placeholder="100" min="0" max="100" value={filters.max_score}
                onChange={(e) => setFilters({ ...filters, max_score: e.target.value })}
                className="h-8 text-xs" />
            </div>
          </div>
        </Card>

        {/* ── KPI Cards ────────────────────────────────────────── */}
        {kpi && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            <KPICard label="Total Calls" value={kpi.total_calls} icon={FileText} />
            <KPICard label="Assessed" value={kpi.assessed_calls} icon={CheckCircle2} />
            <KPICard label="Avg Score" value={`${kpi.avg_score}%`}
              accent={scoreColor(kpi.avg_score)} icon={TrendingUp} />
            <KPICard label="Pass Rate" value={`${kpi.pass_rate}%`}
              accent={kpi.pass_rate >= 70 ? 'text-emerald-600' : 'text-rose-600'} icon={Award} />
            <KPICard label="Violations" value={kpi.total_violations}
              accent="text-rose-600" icon={AlertTriangle} />
          </div>
        )}

        {/* ── Main Tabs ────────────────────────────────────────── */}
        <Tabs defaultValue="table">
          <TabsList>
            <TabsTrigger value="table">Audit Table</TabsTrigger>
            <TabsTrigger value="charts">Charts</TabsTrigger>
            {!myAgent && <TabsTrigger value="agents">Top Agents</TabsTrigger>}
          </TabsList>

          {/* ── Audit Table ──────────────────────────────────────── */}
          <TabsContent value="table">
            <Card className="border border-slate-200 shadow-sm">
              <div className="p-4 border-b border-slate-100 flex items-center justify-between">
                <p className="text-sm font-semibold text-slate-700">{total} assessments</p>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="assessments-table">
                  <thead>
                    <tr className="border-b border-slate-100 bg-slate-50">
                      {['Call', 'Agent', 'SOP', 'Score', 'Violations', 'Date', 'Actions'].map((h) => (
                        <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-slate-600">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.length === 0 && (
                      <tr><td colSpan={7} className="text-center py-10 text-slate-400">No assessments match the current filters</td></tr>
                    )}
                    {rows.map((row) => (
                      <tr key={row.assessment_id} className="border-b border-slate-50 hover:bg-slate-50 transition-colors">
                        <td className="px-4 py-3 font-medium text-slate-800 max-w-[160px] truncate">{row.call_title}</td>
                        <td className="px-4 py-3 text-slate-600">
                          <div>{row.agent_name}</div>
                          <div className="text-xs text-slate-400">{row.agent_emp_id}</div>
                        </td>
                        <td className="px-4 py-3 text-slate-600 max-w-[130px] truncate">{row.sop_title}</td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-1 rounded-full text-xs font-bold border ${scoreBg(row.overall_score)}`}>
                            {row.overall_score}%
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          {row.violations_count > 0 ? (
                            <span className="text-rose-600 font-semibold">{row.violations_count}</span>
                          ) : (
                            <span className="text-emerald-600">0</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-xs text-slate-500">
                          {row.assessed_at ? new Date(row.assessed_at).toLocaleDateString() : '—'}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex gap-1">
                            <Button size="sm" variant="ghost" className="h-7 text-xs px-2"
                              onClick={() => navigate(`/assessment/${row.call_id}`)}
                              title="View full assessment">
                              <BarChart2 size={13} />
                            </Button>
                            <Button size="sm" variant="ghost" className="h-7 text-xs px-2 text-indigo-600"
                              onClick={() => handleExportPDF(row.call_id)}
                              data-testid={`export-pdf-${row.call_id}`} title="Download PDF">
                              <FileDown size={13} />
                            </Button>
                            <Button size="sm" variant="ghost" className="h-7 text-xs px-2 text-emerald-600"
                              onClick={() => handleExportRowCSV(row.call_id)}
                              data-testid={`export-csv-${row.call_id}`} title="Download CSV">
                              <Download size={13} />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="px-4 py-3 border-t border-slate-100 flex items-center justify-between">
                  <p className="text-xs text-slate-500">Page {page} of {totalPages}</p>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" disabled={page === 1}
                      onClick={() => handlePageChange(page - 1)}>
                      <ChevronLeft size={14} />
                    </Button>
                    <Button size="sm" variant="outline" disabled={page === totalPages}
                      onClick={() => handlePageChange(page + 1)}>
                      <ChevronRight size={14} />
                    </Button>
                  </div>
                </div>
              )}
            </Card>
          </TabsContent>

          {/* ── Charts tab ───────────────────────────────────────── */}
          <TabsContent value="charts">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

              {/* Trend line */}
              <Card className="p-6 border border-slate-200 shadow-sm">
                <h3 className="font-bold text-slate-900 mb-4">Score Trend (Last 30 Days)</h3>
                <ResponsiveContainer width="100%" height={240}>
                  <LineChart data={trends}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                    <XAxis dataKey="date" tick={{ fontSize: 10 }}
                      tickFormatter={(v) => new Date(v).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} />
                    <YAxis tick={{ fontSize: 10 }} domain={[0, 100]} />
                    <Tooltip contentStyle={{ borderRadius: 8, border: '1px solid #E2E8F0' }}
                      labelFormatter={(v) => new Date(v).toLocaleDateString()} />
                    <Legend />
                    <Line type="monotone" dataKey="average_score" stroke="#4338CA" strokeWidth={2}
                      dot={{ r: 3 }} name="Avg Score %" />
                    <Line type="monotone" dataKey="assessments_count" stroke="#10B981" strokeWidth={2}
                      dot={{ r: 3 }} name="# Assessments" />
                  </LineChart>
                </ResponsiveContainer>
              </Card>

              {/* Score distribution */}
              {kpi && (
                <Card className="p-6 border border-slate-200 shadow-sm">
                  <h3 className="font-bold text-slate-900 mb-4">Score Distribution</h3>
                  <ResponsiveContainer width="100%" height={240}>
                    <BarChart data={kpi.score_distribution}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                      <XAxis dataKey="range" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} />
                      <Tooltip contentStyle={{ borderRadius: 8 }} />
                      <Bar dataKey="count" radius={[6, 6, 0, 0]}>
                        {kpi.score_distribution.map((_, i) => (
                          <Cell key={i} fill={COLORS[i % COLORS.length]} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </Card>
              )}

              {/* By SOP */}
              {kpi && kpi.by_sop.length > 0 && (
                <Card className="p-6 border border-slate-200 shadow-sm">
                  <h3 className="font-bold text-slate-900 mb-4">Performance by SOP</h3>
                  <ResponsiveContainer width="100%" height={240}>
                    <BarChart data={kpi.by_sop} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                      <XAxis type="number" domain={[0, 100]} tick={{ fontSize: 10 }} />
                      <YAxis dataKey="title" type="category" tick={{ fontSize: 9 }} width={110} />
                      <Tooltip contentStyle={{ borderRadius: 8 }} />
                      <Bar dataKey="avg_score" fill="#4338CA" radius={[0, 6, 6, 0]} name="Avg Score %" />
                    </BarChart>
                  </ResponsiveContainer>
                </Card>
              )}

              {/* Pass/Fail pie */}
              {kpi && kpi.assessed_calls > 0 && (
                <Card className="p-6 border border-slate-200 shadow-sm">
                  <h3 className="font-bold text-slate-900 mb-4">Pass / Fail Split</h3>
                  <ResponsiveContainer width="100%" height={240}>
                    <PieChart>
                      <Pie
                        data={[
                          { name: 'Pass (≥70%)', value: Math.round(kpi.assessed_calls * kpi.pass_rate / 100) },
                          { name: 'Fail (<70%)',  value: kpi.assessed_calls - Math.round(kpi.assessed_calls * kpi.pass_rate / 100) },
                        ]}
                        cx="50%" cy="50%" outerRadius={80} dataKey="value"
                        label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                        labelLine={false}>
                        <Cell fill="#10B981" /><Cell fill="#EF4444" />
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </Card>
              )}
            </div>
          </TabsContent>

          {/* ── Top Agents tab ───────────────────────────────────── */}
          {!myAgent && (
            <TabsContent value="agents">
              <Card className="border border-slate-200 shadow-sm">
                <table className="w-full text-sm" data-testid="top-agents-table">
                  <thead>
                    <tr className="border-b border-slate-100 bg-slate-50">
                      {['Rank', 'Agent', 'Calls', 'Assessed', 'Avg Score'].map((h) => (
                        <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-slate-600">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {(kpi?.top_agents || []).length === 0 && (
                      <tr><td colSpan={5} className="text-center py-10 text-slate-400">No agent data yet</td></tr>
                    )}
                    {(kpi?.top_agents || []).map((ag, idx) => (
                      <tr key={ag.agent_id} className="border-b border-slate-50 hover:bg-slate-50">
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold
                            ${idx === 0 ? 'bg-yellow-100 text-yellow-700'
                              : idx === 1 ? 'bg-slate-100 text-slate-700'
                              : idx === 2 ? 'bg-orange-100 text-orange-700'
                              : 'bg-slate-50 text-slate-500'}`}>
                            {idx + 1}
                          </span>
                        </td>
                        <td className="px-4 py-3 font-medium text-slate-800">{ag.name}</td>
                        <td className="px-4 py-3 text-slate-600">{ag.call_count}</td>
                        <td className="px-4 py-3 text-slate-600">{ag.assessed_calls}</td>
                        <td className="px-4 py-3">
                          <span className={`font-bold ${scoreColor(ag.avg_score)}`}>{ag.avg_score}%</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Card>
            </TabsContent>
          )}
        </Tabs>

        {loading && (
          <div className="text-center py-8 text-slate-500">Loading reports…</div>
        )}
      </div>
    </Layout>
  );
};

export default Reports;
